import { useState, useEffect, useCallback } from 'react'
import {
  Plus,
  Calendar,
  Users,
  Video,
  Clock,
  ChevronDown,
  ChevronUp,
  X,
  Edit2,
  Trash2,
  RefreshCw,
  FileSignature,
  ShieldCheck
} from 'lucide-react'
import toast from 'react-hot-toast'
import eventService from '../../services/eventService'
import eventTutoringService from '../../services/eventTutoringService'
import CreateCoachEventModal from './CreateCoachEventModal'
import ConfirmDialog from './ConfirmDialog'
import EventTutoringSigningModal from '../eventTutoring/EventTutoringSigningModal'

// A coach event needs the host/tutor agreement when eBUCIS is NOT sponsoring it (the new,
// platform-not-sponsor regime). Sponsored events use the existing waiver instead. The eventType guard
// mirrors the server regime (only COACHING is a non-sponsored tutoring session) so the button can
// never appear on an event the server would reject with WRONG_AUTHORIZATION_REGIME.
const needsHostAgreement = (series) =>
  !series.isCancelled &&
  series.sponsorshipType !== 'DEAVERIS_SPONSORED' &&
  series.eventType === 'COACHING'

const RECURRENCE_LABELS = {
  NONE: 'One-time',
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  BIWEEKLY: 'Biweekly',
  MONTHLY: 'Monthly'
}

function formatDateTime(dateStr) {
  return new Date(dateStr).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

export default function CoachEventsSection() {
  const [events, setEvents] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingSeries, setEditingSeries] = useState(null)
  const [expandedSeries, setExpandedSeries] = useState(new Set())
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  })
  // Host/tutor authorization status per series (seriesId -> boolean ready) and the series currently
  // being signed.
  const [hostReady, setHostReady] = useState({})
  const [signingHostFor, setSigningHostFor] = useState(null)

  const showConfirm = (title, message, onConfirm) => {
    setConfirmDialog({ isOpen: true, title, message, onConfirm })
  }
  const closeConfirm = () =>
    setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null })

  // Fetch host/tutor authorization status for the events that require it (non-sponsored, the new
  // platform-not-sponsor regime). Best-effort per series; a failed lookup leaves the row neutral.
  const loadHostReadiness = useCallback(async (seriesList) => {
    const eligible = (seriesList || []).filter(needsHostAgreement)
    const entries = await Promise.all(
      eligible.map(async (s) => {
        try {
          const r = await eventTutoringService.getHostReadiness(s.id)
          return [s.id, r?.ready === true]
        } catch {
          return [s.id, undefined]
        }
      })
    )
    setHostReady(Object.fromEntries(entries))
  }, [])

  const fetchEvents = useCallback(async () => {
    setIsLoading(true)
    try {
      const { data } = await eventService.getCoachEvents()
      const list = data.events || []
      setEvents(list)
      // Fire-and-forget readiness load; swallow any top-level rejection so it can't surface as an
      // unhandled promise rejection (per-series errors are already handled inside loadHostReadiness).
      loadHostReadiness(list).catch(() => {})
    } catch (error) {
      toast.error('Failed to load events')
    } finally {
      setIsLoading(false)
    }
  }, [loadHostReadiness])

  useEffect(() => {
    fetchEvents()
  }, [fetchEvents])

  const handleDelete = (seriesId) => {
    showConfirm(
      'Cancel Event Series',
      'Are you sure you want to cancel this event series? All future occurrences will be cancelled and registered clients will be notified.',
      async () => {
        closeConfirm()
        try {
          await eventService.deleteCoachEvent(seriesId)
          toast.success('Event cancelled')
          fetchEvents()
        } catch (error) {
          toast.error(error.response?.data?.error || 'Failed to cancel event')
        }
      }
    )
  }

  const handlePermanentDelete = (seriesId) => {
    showConfirm(
      'Permanently Remove Event',
      'This will permanently delete this event series, all its occurrences, and all registration data. This action cannot be undone.',
      async () => {
        closeConfirm()
        try {
          await eventService.permanentDeleteCoachEvent(seriesId)
          toast.success('Event permanently removed')
          fetchEvents()
        } catch (error) {
          toast.error(error.response?.data?.error || 'Failed to remove event')
        }
      }
    )
  }

  const handleCancelOccurrence = (occurrenceId) => {
    showConfirm(
      'Cancel Session',
      'Are you sure you want to cancel this session? Registered clients will be notified of the cancellation.',
      async () => {
        closeConfirm()
        try {
          await eventService.coachCancelOccurrence(occurrenceId, { reason: 'Cancelled by coach' })
          toast.success('Session cancelled')
          fetchEvents()
        } catch (error) {
          toast.error(error.response?.data?.error || 'Failed to cancel')
        }
      }
    )
  }

  const toggleExpand = (seriesId) => {
    setExpandedSeries((prev) => {
      const next = new Set(prev)
      if (next.has(seriesId)) next.delete(seriesId)
      else next.add(seriesId)
      return next
    })
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
      <div className="p-4 border-b dark:border-gray-700 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-violet-600 dark:text-violet-400" />
          My Events
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchEvents}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg"
          >
            <Plus className="w-4 h-4" />
            New Event
          </button>
        </div>
      </div>

      <div className="divide-y dark:divide-gray-700">
        {isLoading ? (
          <div className="p-6 text-center text-gray-500">Loading events...</div>
        ) : events.length === 0 ? (
          <div className="p-6 text-center">
            <Calendar className="w-10 h-10 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
            <p className="text-sm text-gray-500 dark:text-gray-400">No events yet</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Create an event for your coaching clients
            </p>
          </div>
        ) : (
          events.map((series) => (
            <div key={series.id}>
              <div
                className="flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                onClick={() => toggleExpand(series.id)}
              >
                <button className="text-gray-400">
                  {expandedSeries.has(series.id) ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-sm font-medium truncate ${series.isCancelled ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-gray-900 dark:text-gray-100'}`}
                    >
                      {series.title}
                    </span>
                    {series.isCancelled && (
                      <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                        Cancelled
                      </span>
                    )}
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {RECURRENCE_LABELS[series.recurrenceRule]}
                    </span>
                    {series.meetingLink && <Video className="w-3 h-3 text-gray-400" />}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {series._count?.occurrences || 0} sessions
                  </div>
                </div>
                {/* Readiness is loaded asynchronously: show the signed badge when known-true and the
                    amber "Sign" button only when known-false. While it is still undefined (loading) we
                    render nothing, so a signed event never flashes the amber button before resolving. */}
                {needsHostAgreement(series) && hostReady[series.id] === true && (
                  <span
                    className="p-1 text-green-600 dark:text-green-400"
                    title="Host/tutor agreement signed"
                  >
                    <ShieldCheck className="w-4 h-4" />
                  </span>
                )}
                {needsHostAgreement(series) && hostReady[series.id] === false && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setSigningHostFor(series.id)
                    }}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded"
                    title="Sign the host/tutor agreement for this event"
                  >
                    <FileSignature className="w-3.5 h-3.5" />
                    Sign host agreement
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setEditingSeries(series)
                    setShowCreateModal(true)
                  }}
                  className="p-1 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 rounded"
                  title="Edit series"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    series.isCancelled ? handlePermanentDelete(series.id) : handleDelete(series.id)
                  }}
                  className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded"
                  title={series.isCancelled ? 'Remove permanently' : 'Cancel series'}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {expandedSeries.has(series.id) && series.occurrences?.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-900/50 border-t dark:border-gray-700">
                  {series.occurrences.slice(0, 8).map((occ) => (
                    <div
                      key={occ.id}
                      className={`flex items-center gap-3 px-6 py-2 text-xs ${occ.isCancelled ? 'opacity-50' : ''}`}
                    >
                      <Clock className="w-3 h-3 text-gray-400 flex-shrink-0" />
                      <span className="flex-1 text-gray-700 dark:text-gray-300">
                        {formatDateTime(occ.startTime)}
                      </span>
                      <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {occ._count?.registrations ?? 0}
                      </span>
                      {occ.isCancelled ? (
                        <span className="text-red-500 text-xs">Cancelled</span>
                      ) : (
                        <button
                          onClick={() => handleCancelOccurrence(occ.id)}
                          className="p-0.5 text-gray-400 hover:text-red-500 rounded"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ))}
                  {series.occurrences.length > 8 && (
                    <div className="px-6 py-1.5 text-xs text-gray-400">
                      +{series.occurrences.length - 8} more
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <CreateCoachEventModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false)
          setEditingSeries(null)
        }}
        onCreated={fetchEvents}
        editSeries={editingSeries}
      />
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel="Yes, Cancel"
        onConfirm={confirmDialog.onConfirm}
        onCancel={closeConfirm}
      />
      <EventTutoringSigningModal
        isOpen={!!signingHostFor}
        eventSeriesId={signingHostFor}
        side="host"
        onClose={() => setSigningHostFor(null)}
        onSigned={() => {
          setSigningHostFor(null)
          loadHostReadiness(events)
        }}
      />
    </div>
  )
}
